/**
 * useReportingDashboard
 *
 * Fetches pre-aggregated reporting dashboard data from the backend. The old
 * reporting page loaded every team entry and aggregated in the browser; this
 * hook keeps the UI contract but pushes date/scope/filter work to SQL.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { startOfWeek as dfStartOfWeek } from "date-fns";
import { useMemo } from "react";
import { z } from "zod";
import type { FieldOptions } from "@/features/attorney/views/Entries/filters/FilterMenu";
import type { EntryFilterState } from "@/features/attorney/views/Entries/filters/types";
import { serializeEntryFilters } from "@/features/attorney/views/Entries/filters/urlState";
import type { BillableCategory, BillingCategory } from "@/shared/cache";
import { entryQueryKeys } from "@/shared/cache/entryQueryKeys";
import { useApi } from "@/shared/utils/request";
import { countWeekdays } from "@/shared/utils/workdays";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface DashboardStats {
	totalHours: number;
	exportedHours: number;
	trackedHours: number;
	entryCount: number;
	matterCount: number;
	avgPerDay: number;
}

export interface DailyChartPoint {
	date: string; // YYYY-MM-DD
	dateLabel: string; // "4/7"
	tooltipLabel: string; // "Monday 4/7"
	exported: number;
	tracked: number;
}

export interface MemberRow {
	profId: string;
	profName: string;
	profType: string | null;
	tracked: number;
	exported: number;
	total: number;
	entries: number;
	matters: number;
	avgPerDay: number;
}

export interface ClientRow {
	client: string;
	hours: number;
	exported: number;
	tracked: number;
	entries: number;
}

export interface MatterRow {
	matterId: string;
	matterNumber: string;
	matterDescription: string;
	client: string;
	hours: number;
	entries: number;
	billingCategory: BillingCategory;
	billableCategory: BillableCategory;
}

export interface CategoryBucket {
	hours: number;
	exported: number;
	tracked: number;
	entries: number;
}

export interface DashboardFilters {
	dateRange: { from: Date; to: Date };
	/** "me", "team", "all", or a specific prof_id. */
	scope: string | null;
	filters: EntryFilterState;
	enabled?: boolean;
}

export interface DashboardData {
	stats: DashboardStats;
	dailyChart: DailyChartPoint[];
	memberTable: MemberRow[];
	topClients: ClientRow[];
	topMatters: MatterRow[];
	hoursByBillingCategory: Partial<Record<BillingCategory, CategoryBucket>>;
	hoursByBillableCategory: Partial<Record<BillableCategory, CategoryBucket>>;
	fieldOptions: FieldOptions;
	isLoading: boolean;
	isFetching: boolean;
	error: string | null;
}

const BILLING_CATEGORY_VALUES = [
	"hourly",
	"flat_rate",
	"pro_bono",
	"administrative",
	"internal",
	"unknown",
] as const;
const BILLING_CATEGORY_SET = new Set<string>(BILLING_CATEGORY_VALUES);

const billingCategorySchema = z.preprocess(
	(value) =>
		typeof value === "string" && !BILLING_CATEGORY_SET.has(value)
			? "unknown"
			: value,
	z.enum(BILLING_CATEGORY_VALUES),
);

const BILLABLE_CATEGORY_VALUES = [
	"billable",
	"non_billable",
	"unknown",
] as const;
const BILLABLE_CATEGORY_SET = new Set<string>(BILLABLE_CATEGORY_VALUES);

// "Credit" is retired in the UI: matters the server still categorizes as
// billable_credit display as non-billable.
const billableCategorySchema = z.preprocess((value) => {
	if (value === "billable_credit") return "non_billable";
	return typeof value === "string" && !BILLABLE_CATEGORY_SET.has(value)
		? "unknown"
		: value;
}, z.enum(BILLABLE_CATEGORY_VALUES));

const numberSchema = z.coerce.number().finite();

const categoryBucketSchema = z.object({
	hours: numberSchema,
	exported: numberSchema,
	tracked: numberSchema,
	entries: numberSchema,
});

// Same "Credit" retirement as billableCategorySchema, for keyed bucket maps:
// merge the server's billable_credit bucket into non_billable.
function foldCreditBucket(
	buckets: Record<string, CategoryBucket>,
): Record<string, CategoryBucket> {
	const { billable_credit: credit, ...rest } = buckets;
	if (!credit) return rest;
	const nonBillable = rest.non_billable;
	rest.non_billable = nonBillable
		? {
				hours: nonBillable.hours + credit.hours,
				exported: nonBillable.exported + credit.exported,
				tracked: nonBillable.tracked + credit.tracked,
				entries: nonBillable.entries + credit.entries,
			}
		: credit;
	return rest;
}

function withoutCreditOption(options: FieldOptions): FieldOptions {
	const billableCategory = options.billableCategory?.filter(
		(option) =>
			(typeof option === "string" ? option : option.value) !==
			"billable_credit",
	) as FieldOptions["billableCategory"];
	return billableCategory ? { ...options, billableCategory } : options;
}

const fieldOptionItemSchema = z.object({
	value: z.string(),
	label: z.string(),
	sublabel: z.string().optional(),
});

const fieldOptionListSchema = z.union([
	z.array(z.string()),
	z.array(fieldOptionItemSchema),
]);

const filterOptionsSchema = z.partialRecord(
	z.enum([
		"timekeeper",
		"status",
		"application",
		"matter",
		"client",
		"billableCategory",
	]),
	fieldOptionListSchema,
);

const reportingSummaryResponseSchema = z.object({
	userProfId: z.string(),
	scope: z.string(),
	stats: z.object({
		totalHours: numberSchema,
		exportedHours: numberSchema,
		trackedHours: numberSchema,
		entryCount: numberSchema,
		matterCount: numberSchema,
		avgPerDay: numberSchema.optional(),
	}),
	dailyChart: z.array(
		z.object({
			date: z.string(),
			exported: numberSchema,
			tracked: numberSchema,
		}),
	),
	memberTable: z.array(
		z.object({
			profId: z.string(),
			profName: z.string(),
			profType: z.string().nullable(),
			tracked: numberSchema,
			exported: numberSchema,
			total: numberSchema,
			entries: numberSchema,
			matters: numberSchema,
			avgPerDay: numberSchema,
		}),
	),
	topClients: z.array(
		z.object({
			client: z.string(),
			hours: numberSchema,
			exported: numberSchema,
			tracked: numberSchema,
			entries: numberSchema,
		}),
	),
	topMatters: z.array(
		z.object({
			matterId: z.string(),
			matterNumber: z.string(),
			matterDescription: z.string(),
			client: z.string(),
			hours: numberSchema,
			entries: numberSchema,
			billingCategory: billingCategorySchema,
			billableCategory: billableCategorySchema,
		}),
	),
	hoursByBillingCategory: z.record(z.string(), categoryBucketSchema),
	hoursByBillableCategory: z.record(z.string(), categoryBucketSchema),
	filterOptions: filterOptionsSchema.optional(),
});

type ReportingSummaryResponse = z.infer<typeof reportingSummaryResponseSchema>;

function parseReportingSummaryResponse(
	data: unknown,
): ReportingSummaryResponse {
	const result = reportingSummaryResponseSchema.safeParse(data);
	if (!result.success) {
		const issue = result.error.issues[0];
		const path = issue?.path.length ? issue.path.join(".") : "response";
		throw new Error(
			`Invalid reporting dashboard response: ${path} ${issue?.message ?? "is malformed"}`,
		);
	}
	return result.data;
}

// ─────────────────────────────────────────────
// Date Range Presets
// ─────────────────────────────────────────────

export type DatePreset =
	| "today"
	| "last-7"
	| "this-week"
	| "last-week"
	| "this-month"
	| "last-month"
	| "last-30"
	| "this-quarter"
	| "last-quarter"
	| "this-year";

export function getDateRange(preset: DatePreset): { from: Date; to: Date } {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	// Picks up firm-configured weekStartsOn via date-fns setDefaultOptions in main.tsx
	const startOfWeek = (d: Date) => {
		const s = dfStartOfWeek(d);
		s.setHours(0, 0, 0, 0);
		return s;
	};

	switch (preset) {
		case "today":
			return { from: today, to: today };
		case "last-7": {
			const from = new Date(today);
			from.setDate(from.getDate() - 7);
			return { from, to: today };
		}
		case "this-week": {
			return { from: startOfWeek(today), to: today };
		}
		case "last-week": {
			const end = new Date(startOfWeek(today));
			end.setDate(end.getDate() - 1);
			return { from: startOfWeek(end), to: end };
		}
		case "this-month": {
			return {
				from: new Date(today.getFullYear(), today.getMonth(), 1),
				to: today,
			};
		}
		case "last-month": {
			const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			const to = new Date(today.getFullYear(), today.getMonth(), 0);
			return { from, to };
		}
		case "last-30": {
			const from = new Date(today);
			from.setDate(from.getDate() - 30);
			return { from, to: today };
		}
		case "this-quarter": {
			const q = Math.floor(today.getMonth() / 3);
			return { from: new Date(today.getFullYear(), q * 3, 1), to: today };
		}
		case "last-quarter": {
			const q = Math.floor(today.getMonth() / 3);
			const from = new Date(today.getFullYear(), (q - 1) * 3, 1);
			const to = new Date(today.getFullYear(), q * 3, 0);
			return { from, to };
		}
		case "this-year": {
			return { from: new Date(today.getFullYear(), 0, 1), to: today };
		}
	}
}

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
	today: "Today",
	"last-7": "Last 7 Days",
	"this-week": "This Week",
	"last-week": "Last Week",
	"this-month": "This Month",
	"last-month": "Last Month",
	"last-30": "Last 30 Days",
	"this-quarter": "This Quarter",
	"last-quarter": "Last Quarter",
	"this-year": "Year to Date",
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

export function toDateKey(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey: string): string {
	const [, m, d] = dateKey.split("-").map(Number);
	return `${m}/${d}`;
}

function formatTooltipLabel(dateKey: string): string {
	const [y, m, d] = dateKey.split("-").map(Number);
	const date = new Date(y, m - 1, d);
	const days = [
		"Sunday",
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
	];
	return `${days[date.getDay()]} ${m}/${d}`;
}

function roundHours(value: number): number {
	return Math.round((Number(value) || 0) * 10) / 10;
}

const DEFAULT_FIELD_OPTIONS = {
	status: ["tracked", "reviewed", "exported"],
	billableCategory: ["billable", "non_billable", "unknown"],
} satisfies FieldOptions;

const EMPTY_RESPONSE: ReportingSummaryResponse = {
	userProfId: "",
	scope: "me",
	stats: {
		totalHours: 0,
		exportedHours: 0,
		trackedHours: 0,
		entryCount: 0,
		matterCount: 0,
		avgPerDay: 0,
	},
	dailyChart: [],
	memberTable: [],
	topClients: [],
	topMatters: [],
	hoursByBillingCategory: {},
	hoursByBillableCategory: {},
	filterOptions: DEFAULT_FIELD_OPTIONS,
};

function fillDailyChart(
	points: ReportingSummaryResponse["dailyChart"],
	dateRange: DashboardFilters["dateRange"],
): DailyChartPoint[] {
	const byDate = new Map(points.map((p) => [p.date, p]));
	const out: DailyChartPoint[] = [];
	const d = new Date(dateRange.from);
	while (d <= dateRange.to) {
		const key = toDateKey(d);
		const point = byDate.get(key);
		out.push({
			date: key,
			dateLabel: formatDateLabel(key),
			tooltipLabel: formatTooltipLabel(key),
			exported: roundHours(point?.exported ?? 0),
			tracked: roundHours(point?.tracked ?? 0),
		});
		d.setDate(d.getDate() + 1);
	}
	return out;
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────

export function useReportingDashboard({
	dateRange,
	scope,
	filters,
	enabled = true,
}: DashboardFilters): DashboardData {
	const { makeApiRequest } = useApi();
	const { user } = useAuth();
	const fromKey = toDateKey(dateRange.from);
	const toKey = toDateKey(dateRange.to);
	const serializedFilters = serializeEntryFilters(filters);
	const scopeKey = scope ?? "me";

	const query = useQuery({
		queryKey: entryQueryKeys.reportingDashboard(user?.id, {
			from: fromKey,
			to: toKey,
			scope: scopeKey,
			filter: serializedFilters,
		}),
		queryFn: async () => {
			const res = await makeApiRequest({
				endpoint: "/team/reporting-summary",
				method: "GET",
				queryParams: {
					from: fromKey,
					to: toKey,
					scope: scopeKey,
					filter: serializedFilters,
				},
			});
			if (res.status === "ERROR") {
				throw new Error(res.error || "Failed to load reporting dashboard");
			}
			return parseReportingSummaryResponse(res.data);
		},
		enabled,
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		refetchOnWindowFocus: false,
	});

	return useMemo(() => {
		const raw = query.data ?? EMPTY_RESPONSE;
		const weekdays = countWeekdays(dateRange.from, dateRange.to) || 1;
		const stats: DashboardStats = {
			totalHours: roundHours(raw.stats.totalHours),
			exportedHours: roundHours(raw.stats.exportedHours),
			trackedHours: roundHours(raw.stats.trackedHours),
			entryCount: Number(raw.stats.entryCount) || 0,
			matterCount: Number(raw.stats.matterCount) || 0,
			avgPerDay: roundHours(raw.stats.totalHours / weekdays),
		};

		return {
			stats,
			dailyChart: fillDailyChart(raw.dailyChart, dateRange),
			memberTable: raw.memberTable,
			topClients: raw.topClients,
			topMatters: raw.topMatters,
			hoursByBillingCategory: raw.hoursByBillingCategory,
			hoursByBillableCategory: foldCreditBucket(raw.hoursByBillableCategory),
			fieldOptions: withoutCreditOption({
				...DEFAULT_FIELD_OPTIONS,
				...(raw.filterOptions ?? {}),
			}),
			isLoading: query.isLoading && query.data === undefined,
			isFetching: query.isFetching,
			error: query.error instanceof Error ? query.error.message : null,
		};
	}, [dateRange, query.data, query.error, query.isFetching, query.isLoading]);
}
